import React, { useState, useEffect } from 'react';
import { useNavigate, useParams, Link } from 'react-router-dom';
import { Card, CardContent } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Textarea } from '../../components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../components/ui/select';
import { Checkbox } from '../../components/ui/checkbox';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../../components/ui/dialog';
import { packagesAPI, imagesAPI } from '../../services/api';
import { ArrowLeft, Plus, Trash2, Save, Calendar, MapPin, Home, Plane, Bus, Image, X, Check, Video, Play, ChevronUp, ChevronDown, GripVertical, Eye, EyeOff } from 'lucide-react';

// Helper to check if file is video
const isVideoUrl = (url) => {
  if (!url) return false;
  const ext = url.split('.').pop()?.toLowerCase().split('?')[0];
  return ['mp4', 'webm', 'mov', 'avi', 'mpeg'].includes(ext);
};

// Opções padrão de Incluso (sempre disponíveis)
const DEFAULT_INCLUDED_OPTIONS = [
  'Passagens aéreas Saindo do RJ/SP',
  'Transfer Santarém x Alter (ida e volta)',
  'Translado entre os atrativos',
  'Passeio de barco',
  'Hospedagem (quarto compartilhado)',
  'Camping',
  'Passeio de 4x4',
  'Entradas dos atrativos descritos',
  'Café da manhã',
  'Guia Credenciado Cadastur',
  'Guias locais',
  'Seguro contra acidentes pessoais',
  'Brinde comemorativo',
  'Transporte rodoviário'
];

// Opções padrão de Não Incluso (sempre disponíveis)
const DEFAULT_NOT_INCLUDED_OPTIONS = [
  'Alimentação extra',
  'Serviços opcionais',
  'Tudo que não estiver descrito como incluso',
  'Entradas dos atrativos não descritos'
];

// Pontos de embarque padrão
const DEFAULT_BOARDING_POINTS = [
  'Rodoviária Novo Rio',
  'Casa do Alemão na W Luiz',
  'Prefeitura de Jardim Primavera'
];

// Cidades de embarque padrão
const DEFAULT_DEPARTURE_CITIES = [
  'Rio de Janeiro',
  'São Paulo'
];

// Países padrão para viagens internacionais (ordem alfabética)
const DEFAULT_COUNTRIES = [
  'Bolívia',
  'Chile',
  'Colômbia',
  'México',
  'Venezuela'
];

const AdminPackageForm = () => {
  const navigate = useNavigate();
  const { id } = useParams();
  const isEditing = !!id;
  
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  
  // Image Library Modal
  const [showImageLibrary, setShowImageLibrary] = useState(false);
  const [libraryImages, setLibraryImages] = useState([]);
  const [loadingImages, setLoadingImages] = useState(false);
  const [imageSelectTarget, setImageSelectTarget] = useState(null); // 'main' or 'gallery'
  
  // Configurações carregadas
  const [availableIncluded, setAvailableIncluded] = useState(DEFAULT_INCLUDED_OPTIONS);
  const [availableNotIncluded, setAvailableNotIncluded] = useState(DEFAULT_NOT_INCLUDED_OPTIONS);
  const [availableBoardingPoints, setAvailableBoardingPoints] = useState(DEFAULT_BOARDING_POINTS);
  const [availableDepartureCities, setAvailableDepartureCities] = useState(DEFAULT_DEPARTURE_CITIES);
  const [availableCountries, setAvailableCountries] = useState(DEFAULT_COUNTRIES);
  const [newCountry, setNewCountry] = useState('');

  const [formData, setFormData] = useState({
    name: '',
    category: 'nacional-aereo',
    price: '',
    image: '',
    gallery_images: [],
    short_description: '',
    description: '',
    departure: 'Rio de Janeiro ou São Paulo',
    duration_days: '',
    payment: 'PIX, Boleto ou Cartão em até 12x',
    included: [],
    not_included: [],
    itinerary: [{ day: 1, title: '', description: '' }],
    dates: [{ id: 'd1', date: '', start_date: '', end_date: '', max_spots: 20, spots_available: 20, available: true, badge: '' }],
    boarding_points: [],
    departure_cities: [],
    countries: [],  // Países para viagens internacionais
    requires_passport: false, // Flag para viagens internacionais fora da América do Sul
    accommodation_options: [
      { type: 'compartilhado', enabled: true, additional_price: 0, label: '' },
      { type: 'casal', enabled: true, additional_price: 350, label: '' },
      { type: 'privativo', enabled: true, additional_price: 600, label: '' }
    ],
    room_surcharges: {
      compartilhado: 0,
      casal: 350,  // Padrão aéreo nacional
      individual: 600  // Padrão aéreo nacional
    },
    // Campos SEO
    slug: '',
    seo_title: '',
    seo_description: '',
    // Valor do transporte aéreo (para modalidade SEM AÉREO)
    air_cost: ''
  });

  useEffect(() => {
    const auth = sessionStorage.getItem('adminAuth');
    if (!auth) {
      navigate('/admin/login');
      return;
    }
    
    // Carregar configurações salvas
    const savedSettings = localStorage.getItem('adminSettings');
    if (savedSettings) {
      const parsed = JSON.parse(savedSettings);
      
      // Carregar opções disponíveis das configurações
      if (parsed.includedOptions && parsed.includedOptions.length > 0) {
        setAvailableIncluded(parsed.includedOptions.map(o => o.text || o));
      }
      if (parsed.notIncludedOptions && parsed.notIncludedOptions.length > 0) {
        setAvailableNotIncluded(parsed.notIncludedOptions.map(o => o.text || o));
      }
      if (parsed.boardingPoints && parsed.boardingPoints.length > 0) {
        setAvailableBoardingPoints(parsed.boardingPoints);
      }
      if (parsed.departureCities && parsed.departureCities.length > 0) {
        setAvailableDepartureCities(parsed.departureCities);
      }
    }
    
    if (isEditing) {
      loadPackage();
    }
  }, [navigate, isEditing]);

  const loadPackage = async () => {
    try {
      setLoading(true);
      const pkg = await packagesAPI.getById(id, true);
      
      setFormData({
        name: pkg.name || '',
        category: pkg.category || 'nacional-aereo',
        price: pkg.price?.toString() || '',
        image: pkg.image || '',
        gallery_images: pkg.gallery_images || [],
        short_description: pkg.short_description || '',
        description: pkg.description || '',
        departure: pkg.departure || 'Rio de Janeiro ou São Paulo',
        duration_days: pkg.duration_days?.toString() || '',
        payment: pkg.payment || 'PIX, Boleto ou Cartão em até 12x',
        included: pkg.included || [],
        not_included: pkg.not_included || [],
        itinerary: pkg.itinerary?.length > 0 ? pkg.itinerary : [{ day: 1, title: '', description: '' }],
        dates: pkg.dates?.length > 0 ? pkg.dates.map(d => ({
          ...d,
          hidden: d.hidden || false,
          start_date: d.start_date?.split('T')[0] || '',
          end_date: d.end_date?.split('T')[0] || ''
        })) : [{ id: 'd1', date: '', start_date: '', end_date: '', max_spots: 40, spots_available: 40, available: true, hidden: false, badge: '' }],
        boarding_points: pkg.boarding_points || [],
        // Normalização: aceita lista antiga [{name, enabled}, ...] OU nova [str, ...]
        departure_cities: (pkg.departure_cities || []).map(dc =>
          typeof dc === 'string' ? dc : (dc?.name || '')
        ).filter(Boolean),
        countries: pkg.countries || [],  // Carregar países
        requires_passport: pkg.requires_passport || false,
        accommodation_options: pkg.accommodation_options?.length > 0 
          ? pkg.accommodation_options.map(opt => ({
              ...opt,
              label: opt.label || ''  // Garantir que o campo label exista
            }))
          : [
              { type: 'compartilhado', enabled: true, additional_price: 0, label: '' },
              { type: 'casal', enabled: true, additional_price: 500, label: '' },
              { type: 'privativo', enabled: true, additional_price: 800, label: '' }
            ],
        room_surcharges: pkg.room_surcharges || {
          compartilhado: 0,
          casal: pkg.category?.includes('rodoviario') ? 100 : 350,
          individual: pkg.category?.includes('rodoviario') ? 150 : 600
        },
        // Campos SEO
        slug: pkg.slug || '',
        seo_title: pkg.seo_title || '',
        seo_description: pkg.seo_description || '',
        air_cost: pkg.air_cost != null ? pkg.air_cost.toString() : ''
      });
    } catch (error) {
      console.error('Erro ao carregar pacote:', error);
      alert('Erro ao carregar pacote');
    } finally {
      setLoading(false);
    }
  };

  const loadLibraryImages = async () => {
    try {
      setLoadingImages(true);
      const images = await imagesAPI.getAll();
      setLibraryImages(images);
    } catch (error) {
      console.error('Erro ao carregar imagens:', error);
    } finally {
      setLoadingImages(false);
    }
  };

  const openImageLibrary = (target) => {
    setImageSelectTarget(target);
    setShowImageLibrary(true);
    loadLibraryImages();
  };

  const selectImage = (imageUrl) => {
    const fullUrl = imageUrl.startsWith('/') ? `${process.env.REACT_APP_BACKEND_URL}${imageUrl}` : imageUrl;
    
    if (imageSelectTarget === 'main') {
      setFormData(prev => ({ ...prev, image: fullUrl }));
      setShowImageLibrary(false);
    } else if (imageSelectTarget === 'gallery') {
      if (formData.gallery_images.length < 20) {
        setFormData(prev => ({
          ...prev,
          gallery_images: [...prev.gallery_images, { url: fullUrl, title: '' }]
        }));
      } else {
        alert('Máximo de 20 itens na galeria');
      }
    }
  };

  const removeGalleryImage = (index) => {
    setFormData(prev => ({
      ...prev,
      gallery_images: prev.gallery_images.filter((_, i) => i !== index)
    }));
  };

  // Mover imagem para cima
  const moveImageUp = (index) => {
    if (index === 0) return;
    setFormData(prev => {
      const newGallery = [...prev.gallery_images];
      [newGallery[index - 1], newGallery[index]] = [newGallery[index], newGallery[index - 1]];
      return { ...prev, gallery_images: newGallery };
    });
  };

  // Mover imagem para baixo
  const moveImageDown = (index) => {
    setFormData(prev => {
      if (index === prev.gallery_images.length - 1) return prev;
      const newGallery = [...prev.gallery_images];
      [newGallery[index], newGallery[index + 1]] = [newGallery[index + 1], newGallery[index]];
      return { ...prev, gallery_images: newGallery };
    });
  };

  // Drag and drop handlers
  const [draggedIndex, setDraggedIndex] = useState(null);

  const handleDragStart = (e, index) => {
    setDraggedIndex(index);
    e.dataTransfer.effectAllowed = 'move';
    e.dataTransfer.setData('text/plain', index);
  };

  const handleDragOver = (e, index) => {
    e.preventDefault();
    if (draggedIndex === null || draggedIndex === index) return;
    
    // Reorder while dragging
    setFormData(prev => {
      const newGallery = [...prev.gallery_images];
      const draggedItem = newGallery[draggedIndex];
      newGallery.splice(draggedIndex, 1);
      newGallery.splice(index, 0, draggedItem);
      setDraggedIndex(index);
      return { ...prev, gallery_images: newGallery };
    });
  };

  const handleDragEnd = () => {
    setDraggedIndex(null);
  };

  // Helper para obter URL da imagem (suporta string ou objeto)
  const getGalleryImageUrl = (item) => {
    return typeof item === 'string' ? item : item?.url;
  };

  // Função para obter valores padrão de acomodação por categoria
  const getDefaultAccommodationPrices = (category) => {
    switch (category) {
      case 'rodoviario':
        return { casal: 80, privativo: 150 };
      case 'aereo-internacional':
        return { casal: 400, privativo: 700 };
      case 'aereo':
      default:
        return { casal: 350, privativo: 600 };
    }
  };

  const handleChange = (field, value) => {
    setFormData(prev => {
      const newState = { ...prev, [field]: value };
      
      // Se a categoria mudou, atualiza os valores padrão de acomodação
      if (field === 'category') {
        const defaultPrices = getDefaultAccommodationPrices(value);
        newState.accommodation_options = prev.accommodation_options.map(opt => {
          if (opt.type === 'casal') {
            return { ...opt, additional_price: defaultPrices.casal };
          }
          if (opt.type === 'privativo') {
            return { ...opt, additional_price: defaultPrices.privativo };
          }
          return opt;
        });
        newState.room_surcharges = {
          compartilhado: 0,
          casal: defaultPrices.casal,
          individual: defaultPrices.privativo
        };
        
        // Auto-gerar campos SEO quando categoria muda
        if (prev.name) {
          const seoData = generateSeoFields(prev.name, value, prev.dates?.[0]?.start_date);
          newState.slug = newState.slug || seoData.slug;
          newState.seo_title = newState.seo_title || seoData.seo_title;
          newState.seo_description = newState.seo_description || seoData.seo_description;
        }
      }
      
      // Auto-gerar campos SEO quando nome muda
      if (field === 'name' && value) {
        const seoData = generateSeoFields(value, prev.category, prev.dates?.[0]?.start_date);
        // Só auto-preencher se os campos estiverem vazios
        if (!prev.slug) newState.slug = seoData.slug;
        if (!prev.seo_title) newState.seo_title = seoData.seo_title;
        if (!prev.seo_description) newState.seo_description = seoData.seo_description;
      }
      
      return newState;
    });
  };

  // Função para gerar slug a partir do nome (remove acentos, espaços -> hífens)
  const generateSlug = (name) => {
    if (!name) return '';
    return name
      .toLowerCase()
      .normalize('NFD')
      .replace(/[\u0300-\u036f]/g, '') // Remove acentos
      .replace(/ç/g, 'c') // ç -> c
      .replace(/[^a-z0-9\s-]/g, '') // Remove caracteres especiais exceto espaços e hífens
      .trim() // Remove espaços no início/fim
      .replace(/\s+/g, '-') // Espaços -> hífens
      .replace(/-+/g, '-'); // Múltiplos hífens -> único hífen
  };

  // Função para gerar campos SEO automaticamente
  const generateSeoFields = (name, category, startDate) => {
    const slug = generateSlug(name);
    const isRodoviario = category === 'rodoviario';
    const isPasseio1Dia = category === 'passeio-1-dia';
    
    // Extrair ano da data inicial (se disponível)
    let year = new Date().getFullYear();
    if (startDate) {
      const dateObj = new Date(startDate);
      if (!isNaN(dateObj.getTime())) {
        year = dateObj.getFullYear();
      }
    }
    
    // Gerar título SEO
    let seo_title;
    if (isRodoviario || isPasseio1Dia) {
      seo_title = `Viagem para ${name} saindo do RJ | ${year}`;
    } else {
      seo_title = `Viagem para ${name} saindo do RJ e SP | ${year}`;
    }
    
    // Gerar meta description
    let seo_description;
    if (isRodoviario || isPasseio1Dia) {
      seo_description = `Pacote para ${name} com transporte, hospedagem, passeios e acompanhamento. Saídas do RJ. Vagas limitadas!`;
    } else {
      seo_description = `Pacote completo para ${name} com passagem aérea, hospedagem, passeios e acompanhamento. Saídas do RJ e SP. Vagas limitadas!`;
    }
    
    return { slug, seo_title, seo_description };
  };

  const handleIncludedToggle = (text, checked) => {
    setFormData(prev => {
      if (checked) {
        return { ...prev, included: [...prev.included, text] };
      } else {
        return { ...prev, included: prev.included.filter(i => i !== text) };
      }
    });
  };

  const handleNotIncludedToggle = (text, checked) => {
    setFormData(prev => {
      if (checked) {
        return { ...prev, not_included: [...prev.not_included, text] };
      } else {
        return { ...prev, not_included: prev.not_included.filter(i => i !== text) };
      }
    });
  };

  const handleBoardingPointToggle = (name, checked) => {
    setFormData(prev => {
      if (checked) {
        return { ...prev, boarding_points: [...prev.boarding_points, { name, enabled: true }] };
      } else {
        return { ...prev, boarding_points: prev.boarding_points.filter(bp => bp.name !== name) };
      }
    });
  };

  const handleDepartureCityToggle = (name, checked) => {
    setFormData(prev => {
      if (checked) {
        if (prev.departure_cities.includes(name)) return prev;
        return { ...prev, departure_cities: [...prev.departure_cities, name] };
      } else {
        return { ...prev, departure_cities: prev.departure_cities.filter(n => n !== name) };
      }
    });
  };

  const isBoardingPointSelected = (name) => {
    return formData.boarding_points.some(bp => bp.name === name);
  };

  const isDepartureCitySelected = (name) => {
    return formData.departure_cities.includes(name);
  };

  const handleItineraryChange = (index, subfield, value) => {
    setFormData(prev => {
      const newItinerary = [...prev.itinerary];
      newItinerary[index] = { ...newItinerary[index], [subfield]: value };
      return { ...prev, itinerary: newItinerary };
    });
  };

  // Helper function to format date with day of week
  const formatDateWithDayOfWeek = (dateStr) => {
    const date = new Date(dateStr + 'T12:00:00');
    const daysOfWeek = ['Domingo', 'Segunda-feira', 'Terça-feira', 'Quarta-feira', 'Quinta-feira', 'Sexta-feira', 'Sábado'];
    const months = ['Janeiro', 'Fevereiro', 'Março', 'Abril', 'Maio', 'Junho', 'Julho', 'Agosto', 'Setembro', 'Outubro', 'Novembro', 'Dezembro'];
    
    const dayOfWeek = daysOfWeek[date.getDay()];
    const day = date.getDate();
    const month = months[date.getMonth()];
    
    return `${dayOfWeek}, ${day} de ${month}`;
  };

  // Calculate number of days between two dates
  const calculateDays = (startDateStr, endDateStr) => {
    const startDate = new Date(startDateStr + 'T12:00:00');
    const endDate = new Date(endDateStr + 'T12:00:00');
    const diffTime = Math.abs(endDate - startDate);
    const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24)) + 1; // +1 to include both days
    return diffDays;
  };

  // Generate itinerary based on dates
  const generateItinerary = (startDateStr, endDateStr) => {
    const days = calculateDays(startDateStr, endDateStr);
    const newItinerary = [];
    
    for (let i = 0; i < days; i++) {
      const currentDate = new Date(startDateStr + 'T12:00:00');
      currentDate.setDate(currentDate.getDate() + i);
      
      const dateFormatted = formatDateWithDayOfWeek(currentDate.toISOString().split('T')[0]);
      
      newItinerary.push({
        day: i + 1,
        title: dateFormatted,
        description: ''
      });
    }
    
    return newItinerary;
  };

  const handleDateChange = (index, subfield, value) => {
    setFormData(prev => {
      const newDates = [...prev.dates];
      newDates[index] = { ...newDates[index], [subfield]: value };
      
      let newDurationDays = prev.duration_days;
      let newItinerary = prev.itinerary;
      
      const isPasseio1Dia = prev.category === 'passeio-1-dia';
      
      // Se for "Passeio de 1 dia" e preencheu start_date, auto-preencher end_date igual
      if (isPasseio1Dia && subfield === 'start_date' && value) {
        newDates[index].end_date = value;
        newDurationDays = '1';
      }
      
      // Auto-preencher "Exibição", "Duração" e "Itinerário" baseado nas datas
      if ((subfield === 'start_date' || subfield === 'end_date') && newDates[index].start_date && newDates[index].end_date) {
        const startDate = new Date(newDates[index].start_date + 'T12:00:00');
        const endDate = new Date(newDates[index].end_date + 'T12:00:00');
        
        const months = ['Janeiro', 'Fevereiro', 'Março', 'Abril', 'Maio', 'Junho', 
                       'Julho', 'Agosto', 'Setembro', 'Outubro', 'Novembro', 'Dezembro'];
        
        const startDay = startDate.getDate().toString().padStart(2, '0');
        const endDay = endDate.getDate().toString().padStart(2, '0');
        const startMonth = months[startDate.getMonth()];
        const endMonth = months[endDate.getMonth()];
        const year = startDate.getFullYear();
        
        // Para passeio de 1 dia, mostrar apenas a data
        if (isPasseio1Dia || startMonth === endMonth && startDay === endDay) {
          newDates[index].date = `${startDay} de ${startMonth}/${year}`;
        } else if (startMonth === endMonth) {
          newDates[index].date = `${startDay} a ${endDay} de ${startMonth}/${year}`;
        } else {
          newDates[index].date = `${startDay} de ${startMonth} a ${endDay} de ${endMonth}/${year}`;
        }
        
        // Auto-preencher duração (apenas se for a primeira data ou se não tiver duração ainda)
        if (index === 0 || !prev.duration_days) {
          newDurationDays = calculateDays(newDates[index].start_date, newDates[index].end_date).toString();
        }
        
        // Auto-gerar itinerário (apenas se for a primeira data e o itinerário estiver vazio ou com apenas 1 item vazio)
        if (index === 0 && (prev.itinerary.length === 0 || (prev.itinerary.length === 1 && !prev.itinerary[0].description))) {
          newItinerary = generateItinerary(newDates[index].start_date, newDates[index].end_date);
        }
        
        // Atualizar SEO title com o ano correto (apenas para a primeira data)
        if (index === 0 && prev.name && !prev.seo_title) {
          const seoData = generateSeoFields(prev.name, prev.category, newDates[index].start_date);
          return { 
            ...prev, 
            dates: newDates,
            duration_days: newDurationDays,
            itinerary: newItinerary,
            seo_title: seoData.seo_title
          };
        }
      }
      
      // Validar vagas disponíveis <= máximo
      if (subfield === 'max_spots') {
        const maxSpots = parseInt(value) || 0;
        if (newDates[index].spots_available > maxSpots) {
          newDates[index].spots_available = maxSpots;
        }
      }
      
      if (subfield === 'spots_available') {
        const spotsAvailable = parseInt(value) || 0;
        const maxSpots = parseInt(newDates[index].max_spots) || 40;
        if (spotsAvailable > maxSpots) {
          newDates[index].spots_available = maxSpots;
        }
      }
      
      return { 
        ...prev, 
        dates: newDates,
        duration_days: newDurationDays,
        itinerary: newItinerary
      };
    });
  };

  const handleAccommodationChange = (index, field, value) => {
    setFormData(prev => {
      const newOptions = [...prev.accommodation_options];
      newOptions[index] = { ...newOptions[index], [field]: value };
      return { ...prev, accommodation_options: newOptions };
    });
  };

  const addItineraryDay = () => {
    setFormData(prev => ({
      ...prev,
      itinerary: [...prev.itinerary, { day: prev.itinerary.length + 1, title: '', description: '' }]
    }));
  };

  const removeItineraryDay = (index) => {
    setFormData(prev => ({
      ...prev,
      itinerary: prev.itinerary.filter((_, i) => i !== index).map((item, i) => ({ ...item, day: i + 1 }))
    }));
  };

  const addDate = () => {
    setFormData(prev => ({
      ...prev,
      dates: [...prev.dates, {
        id: `d${Date.now()}`,
        date: '',
        start_date: '',
        end_date: '',
        max_spots: 20,
        spots_available: 20,
        available: true,
        badge: ''
      }]
    }));
  };

  const removeDate = (index) => {
    setFormData(prev => ({
      ...prev,
      dates: prev.dates.filter((_, i) => i !== index)
    }));
  };

  const addCustomIncluded = () => {
    const newItem = prompt('Nova opção de "Incluso":');
    if (newItem && newItem.trim()) {
      setFormData(prev => ({
        ...prev,
        included: [...prev.included, newItem.trim()]
      }));
    }
  };

  const addCustomNotIncluded = () => {
    const newItem = prompt('Nova opção de "Não Incluso":');
    if (newItem && newItem.trim()) {
      setFormData(prev => ({
        ...prev,
        not_included: [...prev.not_included, newItem.trim()]
      }));
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!formData.name || !formData.price || !formData.image) {
      alert('Preencha os campos obrigatórios: Nome, Preço e Imagem');
      return;
    }

    setSaving(true);
    try {
      // Processar gallery_images - separar URLs simples de objetos com título
      const galleryData = formData.gallery_images || [];
      const simpleGalleryImages = [];
      const galleryWithTitles = [];
      
      galleryData.forEach(item => {
        if (typeof item === 'string') {
          simpleGalleryImages.push(item);
        } else if (item && item.url) {
          // Se tem título, usa o campo gallery (objetos)
          // Se não tem título, pode ir como string simples
          if (item.title && item.title.trim()) {
            galleryWithTitles.push({ url: item.url, title: item.title });
          } else {
            galleryWithTitles.push({ url: item.url, title: '' });
          }
        }
      });

      const packageData = {
        ...formData,
        price: parseFloat(formData.price),
        duration_days: parseInt(formData.duration_days) || 1,
        included: formData.included.filter(i => i && i.trim()),
        not_included: formData.not_included.filter(i => i && i.trim()),
        itinerary: formData.itinerary.filter(i => i.title.trim()),
        // Usar gallery para objetos com título
        gallery_images: galleryWithTitles.length > 0 ? galleryWithTitles : simpleGalleryImages,
        // Garantir que image e main_image sejam sempre iguais
        main_image: formData.image,
        dates: formData.dates.map(d => ({
          ...d,
          max_spots: parseInt(d.max_spots) || 40,
          spots_available: parseInt(d.spots_available) || 0,
          start_date: d.start_date ? d.start_date + 'T00:00:00' : null,
          end_date: d.end_date ? d.end_date + 'T00:00:00' : null
        })),
        boarding_points: formData.boarding_points,
        // Sempre envia lista de strings (nomes das cidades habilitadas)
        departure_cities: (formData.departure_cities || []).map(dc =>
          typeof dc === 'string' ? dc : (dc?.name || '')
        ).filter(Boolean),
        accommodation_options: formData.accommodation_options.map(opt => ({
          ...opt,
          additional_price: parseFloat(opt.additional_price) || 0,
          label: opt.label || ''
        })),
        room_surcharges: formData.room_surcharges,
        // Campos SEO
        slug: formData.slug?.trim() || null,
        seo_title: formData.seo_title?.trim() || null,
        seo_description: formData.seo_description?.trim() || null,
        air_cost: formData.air_cost !== '' && formData.air_cost != null ? parseFloat(formData.air_cost) : 0
      };

      if (isEditing) {
        await packagesAPI.update(id, packageData);
        alert('Pacote atualizado com sucesso!');
      } else {
        await packagesAPI.create(packageData);
        alert('Pacote criado com sucesso!');
      }
      navigate('/admin/pacotes');
    } catch (error) {
      console.error('Erro ao salvar pacote:', error);
      let errorMessage = 'Erro desconhecido';
      if (error.response?.data?.detail) {
        const detail = error.response.data.detail;
        errorMessage = typeof detail === 'string' ? detail : JSON.stringify(detail);
      } else if (error.message) {
        errorMessage = error.message;
      }
      alert('Erro ao salvar pacote: ' + errorMessage);
    } finally {
      setSaving(false);
    }
  };

  const getAccommodationLabel = (type) => {
    const labels = {
      'compartilhado': 'Quarto Compartilhado',
      'casal': 'Quarto de Casal',
      'privativo': 'Quarto Privativo'
    };
    return labels[type] || type;
  };

  // Get min date for end_date based on start_date
  const getMinEndDate = (startDate) => {
    if (!startDate) return '';
    const date = new Date(startDate);
    date.setDate(date.getDate() + 1);
    return date.toISOString().split('T')[0];
  };

  const isRodoviario = formData.category === 'rodoviario';
  const isPasseio1Dia = formData.category === 'passeio-1-dia';

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-100 flex items-center justify-center">
        <div className="animate-spin rounded-full h-16 w-16 border-t-2 border-b-2 border-[#003366]"></div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-100">
      {/* Header */}
      <header className="bg-[#003366] text-white shadow-lg">
        <div className="max-w-7xl mx-auto px-4 py-4 flex items-center justify-between">
          <div className="flex items-center space-x-4">
            <Link to="/admin/pacotes" className="hover:text-[#FFD700]">
              <ArrowLeft className="w-6 h-6" />
            </Link>
            <h1 className="text-xl font-bold">{isEditing ? 'Editar Pacote' : 'Novo Pacote'}</h1>
          </div>
          <Button
            onClick={handleSubmit}
            disabled={saving}
            className="bg-[#FFD700] hover:bg-[#FFC700] text-gray-900"
          >
            <Save className="w-4 h-4 mr-2" />
            {saving ? 'Salvando...' : 'Salvar'}
          </Button>
        </div>
      </header>

      <div className="max-w-4xl mx-auto px-4 py-8">
        <form onSubmit={handleSubmit} className="space-y-8">
          {/* Informações Básicas */}
          <Card>
            <CardContent className="p-6">
              <h2 className="text-lg font-bold text-gray-900 mb-4">Informações Básicas</h2>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div className="md:col-span-2">
                  <Label>Nome do Pacote *</Label>
                  <Input
                    value={formData.name}
                    onChange={(e) => handleChange('name', e.target.value)}
                    placeholder="Ex: Fernando de Noronha"
                    className="mt-1"
                    required
                  />
                </div>
                <div>
                  <Label>Categoria *</Label>
                  <Select value={formData.category} onValueChange={(v) => {
                    handleChange('category', v);
                    // Se for "Passeio de 1 dia", auto-preencher
                    if (v === 'passeio-1-dia') {
                      handleChange('duration_days', '1');
                      handleChange('departure', 'Rio de Janeiro');
                      // Limpar opções de acomodação (não se aplica)
                      handleChange('accommodation_options', []);
                    }
                  }}>
                    <SelectTrigger className="mt-1">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="nacional-aereo">
                        <div className="flex items-center">
                          <Plane className="w-4 h-4 mr-2 text-blue-600" />
                          Nacional - Aéreo
                        </div>
                      </SelectItem>
                      <SelectItem value="internacional-aereo">
                        <div className="flex items-center">
                          <Plane className="w-4 h-4 mr-2 text-purple-600" />
                          Internacional - Aéreo
                        </div>
                      </SelectItem>
                      <SelectItem value="rodoviario">
                        <div className="flex items-center">
                          <Bus className="w-4 h-4 mr-2 text-orange-600" />
                          Rodoviário
                        </div>
                      </SelectItem>
                      <SelectItem value="passeio-1-dia">
                        <div className="flex items-center">
                          <MapPin className="w-4 h-4 mr-2 text-green-600" />
                          Passeio de 1 Dia
                        </div>
                      </SelectItem>
                    </SelectContent>
                  </Select>
                  
                  {/* Checkbox para passaporte obrigatório - só aparece para viagens internacionais */}
                  {formData.category === 'internacional-aereo' && (
                    <div className="mt-3 p-3 bg-red-50 border border-red-200 rounded-lg">
                      <label className="flex items-center space-x-2 cursor-pointer">
                        <input
                          type="checkbox"
                          checked={formData.requires_passport || false}
                          onChange={(e) => handleChange('requires_passport', e.target.checked)}
                          className="w-4 h-4 rounded border-red-300 text-red-600 focus:ring-red-500"
                        />
                        <span className="text-sm font-medium text-red-800">
                          ⚠️ Destino FORA da América do Sul (PASSAPORTE OBRIGATÓRIO)
                        </span>
                      </label>
                      <p className="text-xs text-red-600 mt-1 ml-6">
                        Marque esta opção para destinos como Cancun, Europa, EUA, etc.
                      </p>
                    </div>
                  )}
                  
                  {/* Países incluídos na viagem - só aparece para viagens internacionais */}
                  {formData.category === 'internacional-aereo' && (
                    <div className="mt-3 p-4 bg-blue-50 border border-blue-200 rounded-lg">
                      <Label className="text-blue-900 font-semibold mb-3 block">🌎 Países incluídos na viagem</Label>
                      <p className="text-xs text-blue-700 mb-3">
                        Selecione os países que farão parte deste pacote (ex: Atacama + Salar de Uyuni = Chile e Bolívia)
                      </p>
                      
                      {/* Países selecionados */}
                      {formData.countries && formData.countries.length > 0 && (
                        <div className="flex flex-wrap gap-2 mb-3">
                          {formData.countries.map((country, idx) => (
                            <span key={idx} className="bg-blue-100 text-blue-800 px-3 py-1 rounded-full text-sm flex items-center gap-1">
                              {country}
                              <button
                                type="button"
                                onClick={() => {
                                  const updated = formData.countries.filter((_, i) => i !== idx);
                                  handleChange('countries', updated);
                                }}
                                className="ml-1 text-blue-600 hover:text-blue-800"
                              >
                                <X className="w-3 h-3" />
                              </button>
                            </span>
                          ))}
                        </div>
                      )}
                      
                      {/* Opções de países */}
                      <div className="grid grid-cols-2 sm:grid-cols-3 gap-2 mb-3">
                        {availableCountries.map((country) => (
                          <label key={country} className="flex items-center space-x-2 cursor-pointer p-2 rounded hover:bg-blue-100">
                            <input
                              type="checkbox"
                              checked={(formData.countries || []).includes(country)}
                              onChange={(e) => {
                                const current = formData.countries || [];
                                if (e.target.checked) {
                                  handleChange('countries', [...current, country]);
                                } else {
                                  handleChange('countries', current.filter(c => c !== country));
                                }
                              }}
                              className="w-4 h-4 rounded border-blue-300 text-blue-600 focus:ring-blue-500"
                            />
                            <span className="text-sm text-blue-800">{country}</span>
                          </label>
                        ))}
                      </div>
                      
                      {/* Adicionar outro país */}
                      <div className="flex gap-2">
                        <Input
                          value={newCountry}
                          onChange={(e) => setNewCountry(e.target.value)}
                          placeholder="Outro país..."
                          className="flex-1 text-sm"
                        />
                        <Button
                          type="button"
                          size="sm"
                          disabled={!newCountry.trim()}
                          onClick={() => {
                            if (newCountry.trim()) {
                              const country = newCountry.trim();
                              const current = formData.countries || [];
                              if (!current.includes(country)) {
                                handleChange('countries', [...current, country]);
                              }
                              // Adicionar à lista de países disponíveis se não existir
                              if (!availableCountries.includes(country)) {
                                setAvailableCountries([...availableCountries, country].sort());
                              }
                              setNewCountry('');
                            }
                          }}
                          className="bg-blue-600 hover:bg-blue-700"
                        >
                          <Plus className="w-4 h-4" />
                        </Button>
                      </div>
                    </div>
                  )}
                </div>
                <div>
                  <Label>Preço Base (R$) *</Label>
                  <Input
                    type="number"
                    step="0.01"
                    value={formData.price}
                    onChange={(e) => handleChange('price', e.target.value)}
                    placeholder="5947.00"
                    className="mt-1"
                    required
                    data-testid="package-price-input"
                  />
                </div>
                {/* Valor do Aéreo — usado para calcular a modalidade SEM AÉREO */}
                {(formData.category === 'nacional-aereo' || formData.category === 'internacional-aereo') && (
                  <div>
                    <Label>Valor do Aéreo (R$)</Label>
                    <Input
                      type="number"
                      step="0.01"
                      min="0"
                      value={formData.air_cost}
                      onChange={(e) => handleChange('air_cost', e.target.value)}
                      placeholder="1500.00"
                      className="mt-1"
                      data-testid="package-air-cost-input"
                    />
                    <p className="text-xs text-gray-500 mt-1">
                      Deixe em branco/zero se o pacote só é vendido COM aéreo. Se preenchido, o cliente poderá escolher COM ou SEM aéreo (preço sem aéreo = Preço Base − Valor do Aéreo).
                    </p>
                    {formData.price && formData.air_cost && parseFloat(formData.air_cost) > 0 && (
                      <p className="text-xs text-blue-700 mt-1 font-medium">
                        Preço SEM aéreo: R$ {(parseFloat(formData.price) - parseFloat(formData.air_cost)).toFixed(2).replace('.', ',')}
                      </p>
                    )}
                  </div>
                )}
                <div>
                  <Label>Duração (dias)</Label>
                  <Input
                    type="number"
                    value={formData.duration_days}
                    onChange={(e) => handleChange('duration_days', e.target.value)}
                    placeholder="7"
                    className="mt-1"
                  />
                </div>
                <div>
                  <Label>Local de Embarque (texto exibição)</Label>
                  <Input
                    value={formData.departure}
                    onChange={(e) => handleChange('departure', e.target.value)}
                    placeholder="Rio de Janeiro ou São Paulo"
                    className="mt-1"
                  />
                </div>
                
                {/* Imagem Principal */}
                <div className="md:col-span-2">
                  <Label>Imagem Principal *</Label>
                  <div className="flex gap-2 mt-1">
                    <Input
                      value={formData.image}
                      onChange={(e) => handleChange('image', e.target.value)}
                      placeholder="URL da imagem ou selecione da biblioteca"
                      className="flex-1"
                      required
                    />
                    <Button type="button" onClick={() => openImageLibrary('main')} variant="outline">
                      <Image className="w-4 h-4 mr-2" />
                      Biblioteca
                    </Button>
                  </div>
                  {formData.image && (
                    <img src={formData.image} alt="Preview" className="mt-2 h-32 object-cover rounded-lg" />
                  )}
                </div>

                {/* Galeria de Imagens */}
                <div className="md:col-span-2">
                  <div className="flex items-center justify-between mb-2">
                    <Label>Galeria de Mídia (até 20) - Clique na imagem para adicionar título</Label>
                    <Button 
                      type="button" 
                      onClick={() => openImageLibrary('gallery')} 
                      variant="outline" 
                      size="sm"
                      disabled={formData.gallery_images.length >= 20}
                    >
                      <Plus className="w-4 h-4 mr-1" />
                      Adicionar da Biblioteca
                    </Button>
                  </div>
                  <div className="grid grid-cols-4 gap-3 mt-2">
                    {formData.gallery_images.map((item, index) => {
                      // Suporta tanto string quanto objeto {url, title}
                      const imgUrl = typeof item === 'string' ? item : item.url;
                      const imgTitle = typeof item === 'object' ? item.title : '';
                      const isVideo = isVideoUrl(imgUrl);
                      const isFirst = index === 0;
                      const isLast = index === formData.gallery_images.length - 1;
                      
                      return (
                        <div 
                          key={index} 
                          className={`relative group ${draggedIndex === index ? 'opacity-50' : ''}`}
                          draggable
                          onDragStart={(e) => handleDragStart(e, index)}
                          onDragOver={(e) => handleDragOver(e, index)}
                          onDragEnd={handleDragEnd}
                        >
                          {/* Drag handle */}
                          <div className="absolute top-1 left-1 z-10 cursor-grab opacity-0 group-hover:opacity-100 transition-opacity bg-black/50 rounded p-0.5">
                            <GripVertical className="w-3 h-3 text-white" />
                          </div>
                          
                          {/* Badge de posição para primeira imagem */}
                          {isFirst && (
                            <div className="absolute top-1 left-6 z-10 bg-yellow-500 text-black text-[10px] px-1.5 py-0.5 rounded font-bold">
                              CAPA
                            </div>
                          )}
                          
                          {isVideo ? (
                            <video 
                              src={imgUrl} 
                              className="w-full h-24 object-cover rounded-lg"
                              muted
                              onMouseEnter={(e) => e.target.play()}
                              onMouseLeave={(e) => { e.target.pause(); e.target.currentTime = 0; }}
                            />
                          ) : (
                            <img 
                              src={imgUrl} 
                              alt={imgTitle || `Galeria ${index + 1}`} 
                              className="w-full h-24 object-cover rounded-lg"
                            />
                          )}
                          {isVideo && (
                            <div className="absolute top-1 left-1 bg-black/70 text-white px-1.5 py-0.5 rounded-full flex items-center gap-1 text-xs">
                              <Play className="w-2.5 h-2.5" fill="white" />
                            </div>
                          )}
                          
                          {/* Botões de controle */}
                          <div className="absolute top-1 right-1 flex gap-1 opacity-0 group-hover:opacity-100 transition-opacity">
                            {!isFirst && (
                              <button
                                type="button"
                                onClick={() => moveImageUp(index)}
                                className="bg-blue-500 text-white rounded-full p-1 hover:bg-blue-600"
                                title="Mover para cima"
                              >
                                <ChevronUp className="w-3 h-3" />
                              </button>
                            )}
                            {!isLast && (
                              <button
                                type="button"
                                onClick={() => moveImageDown(index)}
                                className="bg-blue-500 text-white rounded-full p-1 hover:bg-blue-600"
                                title="Mover para baixo"
                              >
                                <ChevronDown className="w-3 h-3" />
                              </button>
                            )}
                            <button
                              type="button"
                              onClick={() => removeGalleryImage(index)}
                              className="bg-red-500 text-white rounded-full p-1 hover:bg-red-600"
                              title="Remover"
                            >
                              <X className="w-3 h-3" />
                            </button>
                          </div>
                          
                          {/* Input para título da imagem */}
                          <input
                            type="text"
                            placeholder="Título (ex: Piedras Rojas)"
                            value={imgTitle}
                            onChange={(e) => {
                              setFormData(prev => {
                                const newGallery = [...prev.gallery_images];
                                newGallery[index] = { url: imgUrl, title: e.target.value };
                                return { ...prev, gallery_images: newGallery };
                              });
                            }}
                            className="absolute bottom-0 left-0 right-0 bg-black/70 text-white text-xs px-2 py-1 rounded-b-lg border-0 focus:outline-none focus:ring-1 focus:ring-[#FFD700]"
                          />
                        </div>
                      );
                    })}
                    {formData.gallery_images.length === 0 && (
                      <div className="col-span-4 text-center py-4 text-gray-500 text-sm border-2 border-dashed rounded-lg">
                        Nenhuma mídia na galeria. Clique em &quot;Adicionar da Biblioteca&quot; para adicionar.
                      </div>
                    )}
                  </div>
                  <p className="text-xs text-gray-500 mt-1">{formData.gallery_images.length}/20 itens</p>
                </div>

                <div className="md:col-span-2">
                  <Label>Descrição Curta</Label>
                  <Textarea
                    value={formData.short_description}
                    onChange={(e) => handleChange('short_description', e.target.value)}
                    placeholder="Uma breve descrição do pacote (aparece nos cards)..."
                    className="mt-1"
                    rows={2}
                  />
                </div>
                
                <div className="md:col-span-2">
                  <Label>Descrição Detalhada</Label>
                  <Textarea
                    value={formData.description}
                    onChange={(e) => handleChange('description', e.target.value)}
                    placeholder="Ex: A cidade das pedras está a cerca de 1400 metros de altitude, cercada por um lindo vale cheio de cachoeiras, grutas, trilhas..."
                    className="mt-1"
                    rows={4}
                  />
                  <p className="text-xs text-gray-500 mt-1">Esta descrição aparece na página de detalhes do pacote, abaixo da seleção de data.</p>
                </div>
              </div>
            </CardContent>
          </Card>

          {/* SEO - Otimização para Mecanismos de Busca */}
          <Card className="border-purple-200 bg-purple-50/30">
            <CardContent className="p-6">
              <h2 className="text-lg font-bold text-gray-900 mb-4 flex items-center gap-2">
                🔍 SEO - Otimização para Google
              </h2>
              <p className="text-sm text-gray-600 mb-4">
                Campos preenchidos automaticamente ao digitar o nome do pacote. Você pode editá-los manualmente se preferir.
              </p>
              <div className="grid grid-cols-1 gap-6">
                <div>
                  <Label>URL Amigável (Slug)</Label>
                  <div className="flex items-center gap-2 mt-1">
                    <span className="text-gray-500 text-sm whitespace-nowrap">toindoali.com.br/pacote/</span>
                    <Input
                      value={formData.slug}
                      onChange={(e) => handleChange('slug', generateSlug(e.target.value))}
                      placeholder="alter-do-chao"
                      className="flex-1"
                    />
                  </div>
                  <p className="text-xs text-gray-500 mt-1">
                    Gerado automaticamente do nome. Sem acentos, espaços viram hífens.
                  </p>
                </div>
                <div>
                  <Label>Título SEO (máx. 60 caracteres)</Label>
                  <Input
                    value={formData.seo_title}
                    onChange={(e) => handleChange('seo_title', e.target.value)}
                    placeholder={formData.category === 'rodoviario' || formData.category === 'passeio-1-dia' 
                      ? "Viagem para [Destino] saindo do RJ | 2026" 
                      : "Viagem para [Destino] saindo do RJ e SP | 2026"}
                    className="mt-1"
                    maxLength={70}
                  />
                  <p className="text-xs text-gray-500 mt-1">
                    {formData.seo_title?.length || 0}/60 - Título que aparece nos resultados do Google
                  </p>
                </div>
                <div>
                  <Label>Meta Descrição (máx. 160 caracteres)</Label>
                  <Textarea
                    value={formData.seo_description}
                    onChange={(e) => handleChange('seo_description', e.target.value)}
                    placeholder={formData.category === 'rodoviario' || formData.category === 'passeio-1-dia'
                      ? "Pacote para [Destino] com transporte, hospedagem, passeios e acompanhamento. Saídas do RJ. Vagas limitadas!"
                      : "Pacote completo para [Destino] com passagem aérea, hospedagem, passeios e acompanhamento. Saídas do RJ e SP. Vagas limitadas!"}
                    className="mt-1"
                    rows={3}
                    maxLength={170}
                  />
                  <p className="text-xs text-gray-500 mt-1">
                    {formData.seo_description?.length || 0}/160 - Descrição que aparece abaixo do título no Google
                  </p>
                </div>
              </div>
            </CardContent>
          </Card>

          {/* Embarque - Condicional baseado na categoria */}
          {!isPasseio1Dia && (
          <Card>
            <CardContent className="p-6">
              <div className="flex items-center space-x-2 mb-4">
                {isRodoviario ? (
                  <Bus className="w-5 h-5 text-orange-600" />
                ) : (
                  <Plane className="w-5 h-5 text-blue-600" />
                )}
                <h2 className="text-lg font-bold text-gray-900">
                  {isRodoviario ? 'Pontos de Embarque (Rodoviário)' : 'Cidades de Embarque (Aéreo)'}
                </h2>
              </div>
              <p className="text-sm text-gray-600 mb-4">
                Selecione os locais de embarque disponíveis para este pacote:
              </p>
              
              {isRodoviario ? (
                <div className="space-y-3">
                  {availableBoardingPoints.map((point, index) => (
                    <div key={index} className="flex items-center space-x-3 p-3 bg-orange-50 rounded-lg">
                      <Checkbox
                        id={`bp-${index}`}
                        checked={isBoardingPointSelected(point)}
                        onCheckedChange={(checked) => handleBoardingPointToggle(point, checked)}
                      />
                      <Label htmlFor={`bp-${index}`} className="flex-1 cursor-pointer">
                        {point}
                      </Label>
                    </div>
                  ))}
                </div>
              ) : (
                <div className="space-y-3">
                  {availableDepartureCities.map((city, index) => (
                    <div key={index} className="flex items-center space-x-3 p-3 bg-blue-50 rounded-lg">
                      <Checkbox
                        id={`dc-${index}`}
                        checked={isDepartureCitySelected(city)}
                        onCheckedChange={(checked) => handleDepartureCityToggle(city, checked)}
                      />
                      <Label htmlFor={`dc-${index}`} className="flex-1 cursor-pointer">
                        {city}
                      </Label>
                    </div>
                  ))}
                </div>
              )}
            </CardContent>
          </Card>
          )}

          {/* Passeio de 1 dia - Info sobre embarque fixo */}
          {isPasseio1Dia && (
            <Card className="bg-green-50 border-green-200">
              <CardContent className="p-6">
                <div className="flex items-center space-x-2 mb-2">
                  <MapPin className="w-5 h-5 text-green-600" />
                  <h2 className="text-lg font-bold text-gray-900">Embarque</h2>
                </div>
                <p className="text-sm text-green-800">
                  <strong>Passeios de 1 dia</strong> têm saída fixa do <strong>Rio de Janeiro</strong>. 
                  Não há opções de acomodação para esta categoria.
                </p>
              </CardContent>
            </Card>
          )}

          {/* Opções de Acomodação - Não aparece para Passeio de 1 dia */}
          {!isPasseio1Dia && (
          <Card>
            <CardContent className="p-6">
              <div className="flex items-center space-x-2 mb-4">
                <Home className="w-5 h-5 text-[#003366]" />
                <h2 className="text-lg font-bold text-gray-900">Opções de Acomodação</h2>
              </div>
              <p className="text-sm text-gray-600 mb-4">
                Configure quais tipos de acomodação estão disponíveis e o valor adicional:
              </p>
              <div className="space-y-4">
                {formData.accommodation_options.map((option, index) => (
                  <div key={index} className="flex flex-col gap-3 p-4 bg-gray-50 rounded-lg">
                    <div className="flex items-center space-x-4">
                      <Checkbox
                        id={`acc-${index}`}
                        checked={option.enabled}
                        onCheckedChange={(checked) => handleAccommodationChange(index, 'enabled', checked)}
                      />
                      <Label htmlFor={`acc-${index}`} className="w-40 font-medium">
                        {getAccommodationLabel(option.type)}
                      </Label>
                      <div className="flex items-center space-x-2">
                        <span className="text-sm text-gray-600">Adicional: R$</span>
                        <Input
                          type="number"
                          step="0.01"
                          value={option.additional_price}
                          onChange={(e) => handleAccommodationChange(index, 'additional_price', e.target.value)}
                          className="w-24"
                          disabled={option.type === 'compartilhado'}
                        />
                      </div>
                    </div>
                    <div className="ml-8 flex items-center gap-2">
                      <Label className="text-sm text-gray-600 whitespace-nowrap">Nome personalizado:</Label>
                      <Input
                        type="text"
                        value={option.label || ''}
                        onChange={(e) => handleAccommodationChange(index, 'label', e.target.value)}
                        className="flex-1"
                        placeholder={option.type === 'compartilhado' ? 'Ex: "Quarto Compartilhado"' : option.type === 'casal' ? 'Ex: "Adicionar Barraca"' : 'Ex: "Barraca + Saco de Dormir"'}
                      />
                    </div>
                  </div>
                ))}
              </div>
              
            </CardContent>
          </Card>
          )}

          {/* O que está Incluso */}
          <Card>
            <CardContent className="p-6">
              <div className="flex items-center justify-between mb-4">
                <h2 className="text-lg font-bold text-gray-900">✓ O que está Incluso</h2>
                <Button type="button" onClick={addCustomIncluded} variant="outline" size="sm">
                  <Plus className="w-4 h-4 mr-1" /> Adicionar Outro
                </Button>
              </div>
              <p className="text-sm text-gray-600 mb-4">
                Marque o que está incluso neste pacote:
              </p>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                {availableIncluded.map((option, index) => (
                  <div key={index} className="flex items-center space-x-3 p-3 bg-green-50 rounded-lg hover:bg-green-100 transition-colors">
                    <Checkbox
                      id={`inc-${index}`}
                      checked={formData.included.includes(option)}
                      onCheckedChange={(checked) => handleIncludedToggle(option, checked)}
                    />
                    <Label htmlFor={`inc-${index}`} className="cursor-pointer text-sm flex-1">
                      {option}
                    </Label>
                  </div>
                ))}
                {/* Itens customizados que não estão na lista padrão */}
                {formData.included.filter(i => !availableIncluded.includes(i)).map((item, index) => (
                  <div key={`custom-${index}`} className="flex items-center justify-between p-3 bg-green-100 rounded-lg">
                    <span className="text-sm flex items-center">
                      <Checkbox checked={true} onCheckedChange={() => handleIncludedToggle(item, false)} className="mr-3" />
                      {item}
                    </span>
                    <Button
                      type="button"
                      onClick={() => handleIncludedToggle(item, false)}
                      variant="ghost"
                      size="sm"
                      className="text-red-600 h-6 w-6 p-0"
                    >
                      <Trash2 className="w-3 h-3" />
                    </Button>
                  </div>
                ))}
              </div>
            </CardContent>
          </Card>

          {/* O que NÃO está Incluso */}
          <Card>
            <CardContent className="p-6">
              <div className="flex items-center justify-between mb-4">
                <h2 className="text-lg font-bold text-gray-900">✗ O que NÃO está Incluso</h2>
                <Button type="button" onClick={addCustomNotIncluded} variant="outline" size="sm">
                  <Plus className="w-4 h-4 mr-1" /> Adicionar Outro
                </Button>
              </div>
              <p className="text-sm text-gray-600 mb-4">
                Marque o que NÃO está incluso neste pacote:
              </p>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                {availableNotIncluded.map((option, index) => (
                  <div key={index} className="flex items-center space-x-3 p-3 bg-red-50 rounded-lg hover:bg-red-100 transition-colors">
                    <Checkbox
                      id={`notinc-${index}`}
                      checked={formData.not_included.includes(option)}
                      onCheckedChange={(checked) => handleNotIncludedToggle(option, checked)}
                    />
                    <Label htmlFor={`notinc-${index}`} className="cursor-pointer text-sm flex-1">
                      {option}
                    </Label>
                  </div>
                ))}
                {/* Itens customizados */}
                {formData.not_included.filter(i => !availableNotIncluded.includes(i)).map((item, index) => (
                  <div key={`custom-${index}`} className="flex items-center justify-between p-3 bg-red-100 rounded-lg">
                    <span className="text-sm flex items-center">
                      <Checkbox checked={true} onCheckedChange={() => handleNotIncludedToggle(item, false)} className="mr-3" />
                      {item}
                    </span>
                    <Button
                      type="button"
                      onClick={() => handleNotIncludedToggle(item, false)}
                      variant="ghost"
                      size="sm"
                      className="text-red-600 h-6 w-6 p-0"
                    >
                      <Trash2 className="w-3 h-3" />
                    </Button>
                  </div>
                ))}
              </div>
            </CardContent>
          </Card>

          {/* Datas e Vagas */}
          <Card>
            <CardContent className="p-6">
              <div className="flex items-center justify-between mb-4">
                <h2 className="text-lg font-bold text-gray-900">Datas e Vagas</h2>
                <Button type="button" onClick={addDate} variant="outline" size="sm">
                  <Plus className="w-4 h-4 mr-1" /> Adicionar Data
                </Button>
              </div>
              <div className="space-y-4">
                {formData.dates.map((date, index) => (
                  <div key={index} className={`p-4 rounded-lg ${date.hidden ? 'bg-gray-200 opacity-60 border-2 border-dashed border-gray-400' : 'bg-gray-50'}`}>
                    <div className="flex items-center justify-between mb-3">
                      <span className="font-medium flex items-center">
                        <Calendar className="w-4 h-4 mr-2" />
                        Data {index + 1}
                        {date.hidden && (
                          <span className="ml-2 text-xs bg-gray-600 text-white px-2 py-0.5 rounded">OCULTA</span>
                        )}
                      </span>
                      <div className="flex items-center gap-1">
                        <Button
                          type="button"
                          onClick={() => handleDateChange(index, 'hidden', !date.hidden)}
                          variant="ghost"
                          size="sm"
                          className={date.hidden ? 'text-blue-600' : 'text-gray-600'}
                          title={date.hidden ? 'Tornar visível ao público' : 'Ocultar do público (sem excluir)'}
                          data-testid={`toggle-hide-date-${index}`}
                        >
                          {date.hidden ? <Eye className="w-4 h-4" /> : <EyeOff className="w-4 h-4" />}
                        </Button>
                        {formData.dates.length > 1 && (
                          <Button type="button" onClick={() => removeDate(index)} variant="ghost" size="sm" className="text-red-600">
                            <Trash2 className="w-4 h-4" />
                          </Button>
                        )}
                      </div>
                    </div>
                    <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                      <div>
                        <Label>Data {isPasseio1Dia ? '' : 'Início '}*</Label>
                        <Input
                          type="date"
                          value={date.start_date || ''}
                          onChange={(e) => handleDateChange(index, 'start_date', e.target.value)}
                          className="mt-1"
                        />
                      </div>
                      {!isPasseio1Dia && (
                      <div>
                        <Label>Data Fim *</Label>
                        <Input
                          type="date"
                          value={date.end_date || ''}
                          min={getMinEndDate(date.start_date)}
                          onChange={(e) => handleDateChange(index, 'end_date', e.target.value)}
                          className="mt-1"
                          disabled={!date.start_date}
                        />
                        {!date.start_date && (
                          <p className="text-xs text-amber-600 mt-1">Selecione a data de início primeiro</p>
                        )}
                      </div>
                      )}
                      <div>
                        <Label>Exibição (auto)</Label>
                        <Input
                          value={date.date}
                          onChange={(e) => handleDateChange(index, 'date', e.target.value)}
                          placeholder="Preenchido automaticamente"
                          className="mt-1 bg-gray-100"
                        />
                      </div>
                      <div>
                        <Label>Máximo de Vagas</Label>
                        <Input
                          type="number"
                          min="1"
                          value={date.max_spots}
                          onChange={(e) => handleDateChange(index, 'max_spots', e.target.value)}
                          className="mt-1"
                        />
                      </div>
                      <div>
                        <Label>Vagas Disponíveis</Label>
                        <Input
                          type="number"
                          min="0"
                          max={date.max_spots}
                          value={date.spots_available}
                          onChange={(e) => handleDateChange(index, 'spots_available', e.target.value)}
                          className="mt-1"
                        />
                        <p className="text-xs text-gray-500 mt-1">Máx: {date.max_spots}</p>
                      </div>
                      <div>
                        <Label>Badge (opcional)</Label>
                        <Input
                          value={date.badge || ''}
                          onChange={(e) => handleDateChange(index, 'badge', e.target.value)}
                          placeholder="Ex: Feriado"
                          className="mt-1"
                        />
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </CardContent>
          </Card>

          {/* Roteiro */}
          <Card>
            <CardContent className="p-6">
              <div className="flex items-center justify-between mb-4">
                <h2 className="text-lg font-bold text-gray-900">Roteiro Detalhado</h2>
                <Button type="button" onClick={addItineraryDay} variant="outline" size="sm">
                  <Plus className="w-4 h-4 mr-1" /> Adicionar Dia
                </Button>
              </div>
              <p className="text-xs text-gray-500 mb-4">
                O título de cada dia será exibido junto com a data e dia da semana correspondente na página do pacote.
              </p>
              <div className="space-y-4">
                {formData.itinerary.map((day, index) => (
                  <div key={index} className="p-4 bg-gray-50 rounded-lg">
                    <div className="flex items-center justify-between mb-3">
                      <span className="font-medium">Dia {day.day}</span>
                      {formData.itinerary.length > 1 && (
                        <Button type="button" onClick={() => removeItineraryDay(index)} variant="ghost" size="sm" className="text-red-600">
                          <Trash2 className="w-4 h-4" />
                        </Button>
                      )}
                    </div>
                    <div className="space-y-3">
                      <Input
                        value={day.title}
                        onChange={(e) => handleItineraryChange(index, 'title', e.target.value)}
                        placeholder="Título do dia (ex: Chegada em Santarém)"
                      />
                      <Textarea
                        value={day.description}
                        onChange={(e) => handleItineraryChange(index, 'description', e.target.value)}
                        placeholder="Descrição das atividades..."
                        rows={3}
                      />
                    </div>
                  </div>
                ))}
              </div>
            </CardContent>
          </Card>

          {/* Submit */}
          <div className="flex justify-end space-x-4">
            <Link to="/admin/pacotes">
              <Button type="button" variant="outline">Cancelar</Button>
            </Link>
            <Button type="submit" disabled={saving} className="bg-[#003366] hover:bg-[#002244] text-white">
              <Save className="w-4 h-4 mr-2" />
              {saving ? 'Salvando...' : (isEditing ? 'Atualizar Pacote' : 'Criar Pacote')}
            </Button>
          </div>
        </form>
      </div>

      {/* Image Library Modal */}
      <Dialog open={showImageLibrary} onOpenChange={setShowImageLibrary}>
        <DialogContent className="max-w-4xl max-h-[80vh] overflow-y-auto">
          <DialogHeader>
            <div className="flex items-center justify-between">
              <DialogTitle>
                {imageSelectTarget === 'main' ? 'Selecionar Imagem Principal' : 'Adicionar Imagens à Galeria'}
              </DialogTitle>
              {imageSelectTarget === 'gallery' && (
                <Button onClick={() => setShowImageLibrary(false)} className="bg-[#003366] hover:bg-[#002244]">
                  Concluir ({formData.gallery_images.length}/20)
                </Button>
              )}
            </div>
          </DialogHeader>
          
          {loadingImages ? (
            <div className="flex items-center justify-center py-12">
              <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-[#003366]"></div>
            </div>
          ) : libraryImages.length === 0 ? (
            <div className="text-center py-12 text-gray-500">
              <div className="flex justify-center gap-2 mb-4">
                <Image className="w-12 h-12 text-gray-400" />
                <Video className="w-12 h-12 text-gray-400" />
              </div>
              <p>Nenhuma mídia na biblioteca.</p>
              <p className="text-sm">Acesse o menu &quot;Biblioteca de Mídia&quot; para fazer upload.</p>
            </div>
          ) : (
            <div className="grid grid-cols-4 gap-4 p-4">
              {libraryImages.map((img) => {
                const fullUrl = img.url.startsWith('/') ? `${process.env.REACT_APP_BACKEND_URL}${img.url}` : img.url;
                // Verificar se já está na galeria (suporta string ou objeto)
                const isSelected = imageSelectTarget === 'gallery' && formData.gallery_images.some(item => 
                  (typeof item === 'string' ? item : item?.url) === fullUrl
                );
                const isVideo = isVideoUrl(img.url) || img.media_type === 'video' || img.content_type?.startsWith('video/');
                return (
                  <div 
                    key={img.id} 
                    className={`relative cursor-pointer group rounded-lg overflow-hidden border-2 transition-all ${
                      isSelected ? 'border-green-500 ring-2 ring-green-300' : 'border-transparent hover:border-[#FFD700]'
                    }`}
                    onClick={() => !isSelected && selectImage(img.url)}
                  >
                    {isVideo ? (
                      <video 
                        src={fullUrl}
                        className="w-full h-24 object-cover"
                        muted
                        onMouseEnter={(e) => e.target.play()}
                        onMouseLeave={(e) => { e.target.pause(); e.target.currentTime = 0; }}
                      />
                    ) : (
                      <img 
                        src={fullUrl}
                        alt={img.alt_text || img.original_name}
                        className="w-full h-24 object-cover"
                      />
                    )}
                    {/* Video indicator */}
                    {isVideo && (
                      <div className="absolute top-1 right-1 bg-black/70 text-white px-1.5 py-0.5 rounded-full flex items-center gap-1 text-xs">
                        <Play className="w-2.5 h-2.5" fill="white" />
                      </div>
                    )}
                    {isSelected && (
                      <div className="absolute inset-0 bg-green-500/30 flex items-center justify-center">
                        <Check className="w-8 h-8 text-white" />
                      </div>
                    )}
                    <div className="absolute bottom-0 left-0 right-0 bg-black/60 text-white text-xs p-1 truncate opacity-0 group-hover:opacity-100 transition-opacity">
                      {img.original_name || img.filename}
                    </div>
                  </div>
                );
              })}
            </div>
          )}
          
          {imageSelectTarget === 'gallery' && (
            <div className="flex justify-end p-4 border-t">
              <Button onClick={() => setShowImageLibrary(false)}>
                Concluir ({formData.gallery_images.length}/20)
              </Button>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default AdminPackageForm;
